import Avatar from '@ui/Avatar';
import { CheckIcon, MessageIcon, ShieldCheckIcon } from '@c/icons';
import Button from '@ui/Button';
import FollowSellerButton from '@c/buttons/FollowSellerButton';
import Link from 'next/link';
import { UserDocument } from '@util/types/firestore/users';
import { useChat } from 'context/ChatContext';
import { Stars } from '@c/dashboard/pages/RatingsPage';
import SellerServiceBadges from '@c/sellerBadges/SellerServiceBadges';
import type { SellerBadges } from '@util/sellerBadges/computeSellerBadges';

const SellerCard = ({
  sellerDoc,
  uid,
  productId,
  sellerBadgeResult,
}: {
  sellerDoc: UserDocument;
  uid?: string;
  productId?: string;
  sellerBadgeResult?: { sellerId: string; badges: SellerBadges };
}) => {
  const { openChatModalWithRecipientAndProductId } = useChat();
  if (!sellerDoc) return null;
  return (
    <div className="flex flex-col justify-between gap-[1.6rem] lg:flex-row">
      <div>
        <div className="flex items-center gap-[1.6rem] whitespace-nowrap" itemScope itemType="https://schema.org/Organization">
          <Link
            href={`/shop-seller/${sellerDoc.username_slug}`}
            className="overflow-clip rounded-full"
          >
            <Avatar size="mlarge" user={sellerDoc} />
          </Link>
          <div className="flex flex-col gap-[0.68rem]">
            <div className="flex items-center gap-[0.6rem]" itemProp="name">
              <Link
                href={`/shop-seller/${sellerDoc.username_slug}`}
                className="sm:text-h4 text-[1.5rem] font-semibold text-brand-blue"
              >
                {sellerDoc.username}
              </Link>
              {sellerDoc?.permissions?.is_verified && (
                <span><ShieldCheckIcon width={16} height={18} fill="rgb(36 46 95)" /></span>
              )}
            </div>
            <div className="flex items-center gap-[0.3rem]" itemScope itemType="https://schema.org/AggregateRating">
              <span itemProp="ratingValue" className="hidden">{sellerDoc.rating ?? 0}</span>
              <span itemProp="reviewCount" className="hidden">{sellerDoc.reviews ?? 0}</span>
              <Stars rating={sellerDoc.rating ?? 0} zoom={0.8} />
              <p className="text-[1.2rem] ml-[0.8rem]">({sellerDoc.reviews} reviews)</p>
            </div>
            {sellerBadgeResult?.sellerId === sellerDoc.uid && (
              <SellerServiceBadges badges={sellerBadgeResult.badges} />
            )}
            <div className="hidden flex items-center gap-[0.4rem] text-brand-light-black text-[0.9em]">
              <span className="font-semibold">
                {sellerDoc.metrics?.live_listings ?? 0}
              </span>
              items listed
              <div className="mx-[0.8rem] h-[2rem] w-px bg-brand-lighter-gray" />
              <span className="font-semibold">
                {sellerDoc.metrics?.num_sold ?? 0}
              </span>
              sales
            </div>
          </div>
        </div>
      </div>
      {/* Follow + Message sit together as the seller's peer actions. Follow is
          the re-engagement hook for a buyer who is looking at this seller's gear
          right now — the storefront had this affordance, the product page did
          not. FollowSellerButton hides itself on the seller's own card and turns
          a logged-out click into the login flow, so it needs no extra guard. */}
      <div className="flex items-center gap-[0.8rem] font-semibold">
        {/* Content-width (not the storefront's fixed 15rem) so it sits naturally
            beside the content-sized "Message Seller" button in this compact card. */}
        <FollowSellerButton sellerId={sellerDoc.uid} height="small" width="normal" />
        {(!uid || uid !== sellerDoc.uid) && (
          <Button
            height="small"
            onClick={() =>
              openChatModalWithRecipientAndProductId({
                recipientUid: sellerDoc.uid,
                productId,
              })
            }
            text="Message Seller"
            type="tertiary"
            leadingIcon={<MessageIcon />}
          ></Button>
        )}
      </div>
    </div>
  );
};

export default SellerCard;
